/* eslint-disable no-bitwise,@typescript-eslint/no-var-requires,@typescript-eslint/explicit-function-return-type */

/**
 * Small functions that are used in various places
 */
'use strict';

import {
    DSLP_PREFIX,
    AI_PREFFIX,
    API_SUFFIX,
    HTTP,
    HTTPS,
    STATS_PREFFIX,
    SCHEME_SEPARATOR,
    PORT_SEPARATOR,
    CREDENTIALS_SEPARATOR,
    WT_SUFFIX
} from './constants/utils.constant';
import {MeetingMode} from './constants/room.constants';
import {hostingAppEnum, screenShareTypeEnum} from './constants/account.constants';
import isEmpty from 'lodash/isEmpty';
import startsWith from 'lodash/startsWith';
import endsWith from 'lodash/endsWith';
import reduce from 'lodash/reduce';
import includes from 'lodash/includes';
import last from 'lodash/last';
import forEach from 'lodash/forEach';
import some from 'lodash/some';
import clone from 'lodash/clone';
import get from 'lodash/get';
import cloneDeep from 'lodash/cloneDeep';
import find from 'lodash/find';

const constants = require('./constants/utils.constant');
const patterns = require('./constants/utils.patterns');
const versionComparer = require('compare-versions');

const IOS_DEVICES = ['iphone', 'ipad'];

const GUID_REGEX = /\/(app|#)\/(\w{2,3}\/)?(\d\/)?(\w{4,})\/?(\w{2,})?/;
const PHONE_NUMBER_INDEX_REGEX = /\/(app|#)\/(\w{2,3}\/)?(\d)(\/|\?)/;
const HOSTING_APP_REGEX = /\/(app|#)\/(\w{2,3})(\/|\?)/;
const BASEURL_REGEX = /\/(app|#)\//;
const backendUrlPlaceholder = 'https://{domain}.techsee.me';
const DESKTOP_SHOW_REGEX = /(?:https?:\/\/)?(?:([^.]+)\.)?desktop\.show/;
const DESKTOP_SHOW_REGEX_WITH_SUFFIX = /(?:https?:\/\/)?(.+)\..+desktop\.show/;

function _getDisplayDetails(deviceInfo) {
    let {width, height} = deviceInfo.browserInfo.meta.screen;

    if (width > height) {
        [width, height] = [height, width];
    }

    const pixelRatio = deviceInfo.browserInfo.meta.devicePixelRatio;
    const aspectRatio = height / width;
    const renderer = deviceInfo.browserInfo.renderer || '';

    return {pixelRatio, aspectRatio, renderer};
}

function _isModeSupported(mode) {
    return [MeetingMode.video, MeetingMode.faceMeet, MeetingMode.videoApplication].includes(mode);
}

// eslint-disable-next-line complexity
function _detectIPhoneVersion(deviceInfo) {
    const {pixelRatio, aspectRatio, renderer} = _getDisplayDetails(deviceInfo);

    if (aspectRatio === 896 / 414) {
        switch (pixelRatio) {
            default:
                return 'XR or XS Max';
            case 2:
                return 'XR';
            case 3:
                return 'XS Max';
        }
    } else if (aspectRatio === 812 / 375) {
        switch (renderer) {
            default:
                return 'X or XS';
            case 'Apple A11 GPU':
                return 'X';
            case 'Apple A12 GPU':
                return 'XS';
        }
    } else if (aspectRatio === 736 / 414) {
        switch (renderer) {
            case 'Apple A8 GPU':
                return '6 Plus';
            case 'Apple A9 GPU':
                return '6s Plus';
            case 'Apple A10 GPU':
                return '7 Plus';
            case 'Apple A11 GPU':
                return '8 Plus';
            default:
                return '6 Plus, 6s Plus, 7 Plus or 8 Plus';
        }
    } else if (aspectRatio === 667 / 375 && pixelRatio === 3) {
        switch (renderer) {
            case 'Apple A8 GPU':
                return '6 Plus (display zoom)';
            case 'Apple A9 GPU':
                return '6s Plus (display zoom)';
            case 'Apple A10 GPU':
                return '7 Plus (display zoom)';
            case 'Apple A11 GPU':
                return '8 Plus (display zoom)';
            default:
                return '6 Plus, 6s Plus, 7 Plus or 8 Plus (display zoom)';
        }
    } else if (aspectRatio === 667 / 375 && pixelRatio === 2) {
        switch (renderer) {
            case 'Apple A8 GPU':
                return '6';
            case 'Apple A9 GPU':
                return '6s';
            case 'Apple A10 GPU':
                return '7';
            case 'Apple A11 GPU':
                return '8';
            default:
                return '6, 6s, 7 or 8';
        }
    } else if (aspectRatio === 1.775) {
        switch (renderer) {
            case 'PowerVR SGX 543':
                return '5 or 5c';
            case 'Apple A7 GPU':
                return '5s';
            case 'Apple A8 GPU':
                return '6 (display zoom)';
            case 'Apple A9 GPU':
                return 'SE or 6s (display zoom)';
            case 'Apple A10 GPU':
                return '7 (display zoom)';
            case 'Apple A11 GPU':
                return '8 (display zoom)';
            default:
                return '5, 5C, 5S, SE or 6, 6s, 7 and 8 (display zoom)';
        }
    } else if (aspectRatio === 1.5 && pixelRatio === 2) {
        switch (renderer) {
            case 'PowerVR SGX 535':
                return '4';
            case 'PowerVR SGX 543':
                return '4s';
            default:
                return '4 or 4s';
        }
    } else if (aspectRatio === 1.5 && pixelRatio === 1) {
        switch (renderer) {
            case 'ALP0298C05':
                return '3GS';
            case 'S5L8900':
                return '1, 3G';
            default:
                return '1, 3G or 3GS';
        }
    }

    return '(Version is not detected)';
}

function _detectIPadVersion(deviceInfo) {
    const {pixelRatio, aspectRatio, renderer} = _getDisplayDetails(deviceInfo);

    if (aspectRatio === 1024 / 768) {
        if (pixelRatio === 1) {
            switch (renderer) {
                default:
                    return '1, 2, or Mini';
                case 'PowerVR SGX 535':
                    return '1';
                case 'PowerVR SGX 543':
                    return '2 or Mini';
            }
        } else {
            switch (renderer) {
                default:
                    return '3, 4, 5, 6, Mini 2, Mini 3, Mini 4, Air 1 or Air 2';
                case 'PowerVR SGX 543':
                    return '3';
                case 'PowerVR SGX 554':
                    return '4';
                case 'Apple A7 GPU':
                    return 'Air, Mini 2 or Mini 3';
                case 'Apple A8X GPU':
                    return 'Air 2';
                case 'Apple A8 GPU':
                    return 'Mini 4';
                case 'Apple A9 GPU':
                    return '5 or Pro 9.7';
                case 'Apple A10 GPU':
                    return '6';
            }
        }
    } else if (aspectRatio === 1112 / 834) {
        return 'Pro 10.5';
    } else if (aspectRatio === 1366 / 1024) {
        switch (renderer) {
            default:
                return 'Pro 12.9 or Pro 12.9 (2nd Gen)';
            case 'Apple A10X GPU':
                return 'Pro 12.9 (2nd Gen)';
            case 'Apple A9 GPU':
                return 'Pro 12.9';
        }
    }

    return '(Version is not detected)';
}

function _isContainingFormula(value) {
    if (isEmpty(value)) {
        return false;
    }

    return /^[@=+-]/.test(value.toString().trim());
}

function hashString(str) {
    let hash = 5381,
        i = str.length;

    while (i) {
        hash = (hash * 33) ^ str.charCodeAt(--i);
    }

    /* JavaScript does bitwise operations (like XOR, above) on 32-bit signed
     * integers. Since we want the results to be always positive, convert the
     * signed int to an unsigned by doing an unsigned bitshift. */
    return hash >>> 0;
}

function _findSchemaInUrl(url) {
    if (!url) {
        return HTTPS;
    }

    return startsWith(url.toLowerCase(), HTTPS) ? HTTP : HTTPS;
}

function _isNewSubDomainPattern(hostName) {
    return hostName.split('.').length > 3;
}

function _buildUrl(url, feFullSubdomain, subdomainPart, restorePrefix, isStat, ignoreApiSuffix = false) {
    // we need to build api/stats:
    // feLastDomainPart = "co.il"
    //     => result is "ynet-api.co.il"
    // if it's stats:
    //     => result is "stats-ynet-api.co.il"
    let feLastDomainPart = feFullSubdomain.substring(feFullSubdomain.indexOf('.') + 1);
    const schema = _findSchemaInUrl(feFullSubdomain);
    const postFix = endsWith(url, '/') ? '/' : '';

    let retVal = `${schema}${restorePrefix}`;

    if (_isNewSubDomainPattern(feFullSubdomain)) {
        feLastDomainPart = feLastDomainPart.split('.').splice(1).join('.');
    }

    if (isStat) {
        retVal = retVal.concat(STATS_PREFFIX);
    }
    // no 'subdomain', it is a FE URL that has a static mapping like app, app1 etc - no need to concat it to the return value
    // stats-api, stats-api1 etc
    if (ignoreApiSuffix) {
        retVal = retVal.concat(`${subdomainPart}.${feLastDomainPart}${postFix}`);
    } else {
        retVal = retVal.concat(`${subdomainPart}${API_SUFFIX}.${feLastDomainPart}${postFix}`);
    }

    return retVal;
}

function _assertHtml(sanitizeHtmlCallback, input, selfClosing, isBlockAllTags, customTagsWhitelist = []) {
    let allowedTags = [];
    let allowedAttributes = {};
    const selfClosingHtmlTags = selfClosing ? selfClosing : constants.DEFAULT_SELF_CLOSING_HTML_TAGS;

    let processedText = input;

    forEach(selfClosingHtmlTags, (tag) => {
        const regex = new RegExp(`<${tag}>`, 'igm');

        processedText = processedText.replace(regex, '');
    });

    if (!isBlockAllTags) {
        allowedTags = customTagsWhitelist.length > 0 ? customTagsWhitelist : constants.DEFAULT_WHITELISTED_HTML_TAGS;

        allowedAttributes = reduce(
            allowedTags,
            (result, value) => {
                if (value === 'a') {
                    result[value] = ['style', 'href', 'target'];
                } else if (value === 'img') {
                    result[value] = ['style', 'src'];
                } else {
                    result[value] = ['style'];
                }

                return result;
            },
            {}
        );
    }

    const validatedHtml = sanitizeHtmlCallback(processedText, {
        allowedTags,
        allowedAttributes,
        selfClosing: selfClosingHtmlTags
    });

    if (processedText.length !== validatedHtml.length) {
        throw new Error('HTML tags are not valid');
    }
}

const utilsApi = (module.exports = {
    resourceTypes: constants.RESOURCE_TYPES,
    subdomains: constants.SUBDOMAINS,
    sdkSubdomains: constants.SDK_SUBDOMAINS,
    hostPlaceholder: constants.HOST_PLACEHOLDER,
    redirectableApplications: constants.REDIRECTABLE_APPLICATIONS,

    assertHtml: _assertHtml,

    hashString: hashString,

    getResourceTypeFromExt: function (url) {
        const imgReg = /(.jpg|.jpeg|.bmp|.png|.gif|.tiff|.tif)$/i,
            pdfReg = /.pdf$/i;

        if (url.match(imgReg)) {
            return constants.RESOURCE_TYPES.image;
        }

        if (url.match(pdfReg)) {
            return constants.RESOURCE_TYPES.pdf;
        }

        return constants.RESOURCE_TYPES.www;
    },

    replaceUrlDomain: function (origUrl, domain, defaultDomain) {
        const replace = domain ? domain : defaultDomain;

        if (origUrl && replace && origUrl.match('^.*' + constants.DOMAIN_PLACEHOLDER + '.*$')) {
            return origUrl.replace(constants.DOMAIN_PLACEHOLDER, replace);
        }

        return origUrl;
    },

    linkify: function (text) {
        // Code for this function taken from angular-sanitize (linky) and
        // simplified for use with this project.
        // /**
        //  * @license AngularJS v1.5.0-build.4514+sha.b77e14b
        //  * (c) 2010-2016 Google, Inc. http://angularjs.org
        //  * License: MIT
        //  */

        const LINKY_URL_REGEXP =
                /((ftp|https?):\/\/|(www\.)|(mailto:)?[A-Za-z0-9._%+-]+@)\S*[^\s.;,(){}<>"\u201d\u2019]/i,
            MAILTO_REGEXP = /^mailto:/i;

        // eslint-disable-next-line init-declarations
        let match, url, i;
        let raw = text;
        const html = [];

        function addText(text, dropQuery) {
            if (!text) {
                return;
            }

            const cleanText = dropQuery ? text.split('?')[0] : text;
            const fileTest =
                /.(doc|docx|odt|pdf|rtf|txt|mp3|wav|wma|ogg|mpa|aif|3g2|3gp|avi|flv|h264|m4v|mkv|mov|mp4|mpg|mpeg|swf|wmv|bmp|gif|svg|tif|tiff|png|jpg|jpeg)$/i;
            const finalText = fileTest.test(cleanText) ? last(cleanText.split('/')) : cleanText;

            html.push(finalText);
        }

        function addLink(url, text) {
            html.push('<a target="_blank" href="', url.replace(/"/g, '&quot;'), '" rel="noreferrer">');
            addText(text, true);
            html.push('</a>');
        }

        while ((match = raw.match(LINKY_URL_REGEXP))) {
            url = match[0];
            if (!match[2] && !match[4]) {
                url = (match[3] ? 'http://' : 'mailto:') + url;
            }
            i = match.index;
            addText(raw.substr(0, i));
            addLink(url, match[0].replace(MAILTO_REGEXP, ''));
            raw = raw.substring(i + match[0].length);
        }

        addText(raw);

        return html.join('');
    },

    getBackendUrl: function (url, envData) {
        if (url.indexOf(constants.HOST_PLACEHOLDER) < 0) {
            return url;
        }

        if (
            envData.hostname &&
            (includes(envData.hostname, 'localhost') || (includes(envData.hostname, 'localhost') && envData.isStat))
        ) {
            return 'http://localhost:3001';
        }

        // example: if url=https://ynet.co.il/hello
        // feFullSubdomain = "ynet.co.il"
        // feFirstSubdomainPart = "ynet"
        let feFullSubdomain = envData.hostname.replace(/(^\w+:|^)\/\//, '').toLowerCase();
        let restorePrefix = '';

        if (feFullSubdomain.indexOf(AI_PREFFIX) >= 0) {
            feFullSubdomain = feFullSubdomain.replace(AI_PREFFIX, '');
        } else if (feFullSubdomain.indexOf(DSLP_PREFIX) >= 0) {
            feFullSubdomain = feFullSubdomain.replace(DSLP_PREFIX, '');
            restorePrefix = DSLP_PREFIX;
        } else if (endsWith(feFullSubdomain, WT_SUFFIX + constants.HOST_SUFFIX)) {
            feFullSubdomain =
                feFullSubdomain.substring(0, feFullSubdomain.length - (WT_SUFFIX + constants.HOST_SUFFIX).length) +
                constants.HOST_SUFFIX;
        }

        const feFirstSubdomainPart = feFullSubdomain.split('.')[0];
        let beSubDomain = constants.SUBDOMAINS[feFirstSubdomainPart];
        const beSubDomainFound = !!beSubDomain;

        if (!beSubDomainFound) {
            if (!envData.useExtendedSubDomainsMap) {
                return _buildUrl(url, feFullSubdomain, feFirstSubdomainPart, restorePrefix, envData.isStat);
            }

            beSubDomain = constants.SDK_SUBDOMAINS[feFirstSubdomainPart] || `${feFirstSubdomainPart}-api`;
        } else if (envData.isStat) {
            return _buildUrl(url, feFullSubdomain, beSubDomain, restorePrefix, true, true);
        }

        const hostSuffix = envData.hostSuffix || constants.HOST_SUFFIX;

        return url.replace(constants.HOST_PLACEHOLDER, restorePrefix + beSubDomain.concat(hostSuffix));
    },

    getAppsBackendUrl(urlOrigin, isStats) {
        if (includes(urlOrigin, 'localhost')) {
            return 'http://localhost:3001';
        }

        let domain = urlOrigin.split('.')[0];

        if (startsWith(domain, 'http')) {
            domain = domain.split('/')[2];
        }
        if (!domain) {
            return urlOrigin;
        }

        const replaceDomain = constants.APPS_SUBDOMAINS[domain.toLowerCase()] || `${domain.toLowerCase()}-api`;

        return backendUrlPlaceholder.replace('{domain}', isStats ? `stats-${replaceDomain}` : replaceDomain);
    },

    getStatsBackendUrl(urlOrigin) {
        return this.getAppsBackendUrl(urlOrigin, true);
    },

    checkValidCoBrowsingUrl: function (url) {
        return new RegExp(patterns.coBrowsingStartUrl).test(url);
    },

    getAdminBackendUrl: function (url, hostname, hostSuffix) {
        if (url.indexOf(constants.HOST_PLACEHOLDER) < 0) {
            return url;
        }

        const feSubdomain = hostname.split('.')[0].toLowerCase();
        const adminBeSubDomain = constants.ADMIN_SUBDOMAINS[feSubdomain] || `${feSubdomain}-admin`;

        if (!adminBeSubDomain) {
            return url;
        }

        return url.replace(constants.HOST_PLACEHOLDER, adminBeSubDomain.concat(hostSuffix || constants.HOST_SUFFIX));
    },

    getMobileClientUrl: function (hostname, domainZoneSuffix) {
        if (hostname === 'localhost:3001') {
            return 'http://localhost:3011/#/';
        }

        const beSubdomain = hostname.split('.')[0].toLowerCase();
        let feSubdomain = constants.DEFAULT_FE[beSubdomain] || beSubdomain.replace('-api', '');

        feSubdomain = domainZoneSuffix ? `${feSubdomain}.${domainZoneSuffix}` : feSubdomain;

        return constants.MOBILE_CLIENT_URL.replace(constants.FE_SUBDOMAIN_PLACEHOLDER, feSubdomain);
    },

    getDashboardClientUrl: function (hostname, domain, domainZoneSuffix) {
        if (domain === 'localhost') {
            return 'http://localhost:3010/#';
        }

        const beSubdomain = hostname.split('.')[0].toLowerCase();
        let feSubdomain = constants.DEFAULT_FE[beSubdomain] || beSubdomain.replace('-api', '');

        feSubdomain = domainZoneSuffix ? `${feSubdomain}.${domainZoneSuffix}` : feSubdomain;

        return constants.DASHBOARD_CLIENT_URL.replace(constants.FE_SUBDOMAIN_PLACEHOLDER, feSubdomain);
    },

    getSubDomainUrl: function getSubDomainUrl(subDomain, path, host = 'techsee.me') {
        const apiUrl = `https://${subDomain}-api.${host}`;
        const fullPath = apiUrl + path;

        return {apiUrl, fullPath};
    },

    detectIOSDeviceVersion(deviceInfo) {
        const model = get(deviceInfo, 'uaData.device.model');

        if (!model || !includes(IOS_DEVICES, model.toLowerCase())) {
            return;
        }

        let version = '';
        const os = deviceInfo.uaData.os;

        if (model === 'iPhone') {
            version = _detectIPhoneVersion(deviceInfo);
        } else if (model === 'iPad') {
            version = _detectIPadVersion(deviceInfo);
        }

        // eslint-disable-next-line consistent-return
        return {model, version, osName: os.name, osVersion: os.version};
    },

    disablePossibleFormulaValuesInCsv: function (csvData) {
        const fieldsWithFormula = some(csvData, (value) => _isContainingFormula(value));

        if (!fieldsWithFormula) {
            return csvData;
        }

        const filteredData = clone(csvData);

        forEach(filteredData, (value, key) => {
            if (_isContainingFormula(value)) {
                filteredData[key] = '`' + value.toString().trim();
            }
        });

        return filteredData;
    },

    findGuidInUrl: function (url) {
        const parts = url && url.match(GUID_REGEX);

        return (parts && parts.length > 4 && parts[4]) || '';
    },

    findHostingAppInUrl: function (url) {
        const parts = url && url.match(HOSTING_APP_REGEX);

        return parts && parts.length > 2 && (parts[2].length === 2 || parts[2].length === 3) && parts[2];
    },

    findThemeInUrl: function (url) {
        const parts = url && url.match(GUID_REGEX);

        return (parts && parts.length > 5 && parts[5]) || '';
    },

    findPhoneNumberIndexInUrl: function (url) {
        const parts = url && url.match(PHONE_NUMBER_INDEX_REGEX);

        return (parts && parts.length > 3 && parts[3]) || '';
    },

    findParamInUrl(url, param) {
        const regEx = new RegExp('[?&]' + param + '=([^&#]*)', 'i');
        const value = regEx.exec(url);

        return value ? value[1] : null;
    },

    createHashedString(stringToHash) {
        let hashedString = 'fail';

        try {
            if (typeof stringToHash === 'string') {
                hashedString = hashString(stringToHash);
            }
            // eslint-disable-next-line no-empty
        } catch (err) {}

        return hashedString;
    },

    sanitizeChatMessage(meta) {
        if (!get(meta, 'message')) {
            return meta;
        }

        const sanitizedMeta = cloneDeep(meta);

        if (!sanitizedMeta.messageHash) {
            sanitizedMeta.messageHash = utilsApi.createHashedString(sanitizedMeta.message);
        }

        delete sanitizedMeta.message;

        return sanitizedMeta;
    },

    getLiveBaseUrl: function (url) {
        // Remove query string
        const queryIndex = url && url.indexOf('?');
        let urlWithoutQuery = queryIndex >= 0 ? url.split('?')[0] : url;

        // Make sure ends with slash
        urlWithoutQuery = urlWithoutQuery && !endsWith(urlWithoutQuery, '/') ? urlWithoutQuery + '/' : urlWithoutQuery;

        const parts = urlWithoutQuery && urlWithoutQuery.match(BASEURL_REGEX);
        const index = (parts && parts.index) || -1;

        return index >= 0 ? urlWithoutQuery.substring(0, index + 1) : urlWithoutQuery;
    },

    getApplicationRedirectionUrl: function (url, application) {
        const baseUrl = utilsApi.getLiveBaseUrl(url);

        if (!baseUrl) {
            return url;
        }

        if (!application) {
            return baseUrl;
        }

        return baseUrl + (endsWith(baseUrl, '/') ? '' : '/') + application;
    },

    convertToSizeString: function (sizeInBytes) {
        return (sizeInBytes / 1000).toFixed(1) + 'kB';
    },

    analyzeClientVersionUpdate: function (currentClientVersion, versionUpdate) {
        return {
            breaking: versionUpdate && versionUpdate.breaking,
            currentIsAnOldVersion: versionUpdate && versionComparer(currentClientVersion, versionUpdate.version) < 0
        };
    },

    compareUnderThreshold(x, y, threshold = 2) {
        const diff = Math.ceil(Math.abs(x - y));

        if (!diff) {
            return false;
        }

        return diff >= threshold;
    },

    protectCustomerNumber(number) {
        return `*****${number.slice(-3)}`;
    },

    protectCustomerEmail(email) {
        const emailArray = email && email.split('@');
        const prefix = emailArray && emailArray[0];
        const domain = emailArray && emailArray[1];
        const prefixLength = prefix && prefix.length - 1;
        const protectedPrefix = '*'.repeat(prefixLength);

        return prefix && prefix[0] && protectedPrefix && domain && `${prefix[0]}${protectedPrefix}@${domain}`;
    },

    getDeskopSharingSubdomain(origin, defaultDomain = '') {
        const regexToUse = _isNewSubDomainPattern(origin) ? DESKTOP_SHOW_REGEX_WITH_SUFFIX : DESKTOP_SHOW_REGEX;
        const match = origin.match(regexToUse);

        if (match) {
            return match[1];
        }

        return defaultDomain;
    },

    getSubDomainFromUrl: function (url) {
        if (!url) {
            return url;
        }

        try {
            let domain = url.split('.')[0];

            const schemeIndex = domain.indexOf(SCHEME_SEPARATOR);

            if (schemeIndex >= 0) {
                domain = domain.substring(schemeIndex + SCHEME_SEPARATOR.length);
            }

            if (domain.indexOf(PORT_SEPARATOR) >= 0) {
                domain = domain.split(PORT_SEPARATOR)[0];
            }

            return domain;
        } catch (err) {
            return '';
        }
    },

    getDomainFromUrl: function (url) {
        if (!url) {
            return url;
        }

        try {
            const match = new RegExp(patterns.domain).exec(url);

            if (!match || !match.length) {
                return null;
            }

            let domain = match[0];

            const schemeIndex = domain.indexOf(SCHEME_SEPARATOR);

            if (schemeIndex >= 0) {
                domain = domain.substring(schemeIndex + SCHEME_SEPARATOR.length);
            }

            const credentialsIndex = domain.indexOf(CREDENTIALS_SEPARATOR);

            if (credentialsIndex >= 0) {
                domain = domain.substring(credentialsIndex + CREDENTIALS_SEPARATOR.length);
            }

            return domain;
        } catch (err) {
            return '';
        }
    },

    getDesktopShowUrl: (subDomain, domainZoneSuffix) => {
        if (subDomain) {
            const url = domainZoneSuffix
                ? `https://${subDomain}.${domainZoneSuffix}.desktop.show`
                : `https://${subDomain}.desktop.show`;

            return {url, displayUrl: url.replace('https://', '')};
        }

        return {url: 'https://desktop.show', displayUrl: 'desktop.show'};
    },

    getTechSeeTermsUrl: (domainSuffix, termsFileName = 'techsee-terms.html', host = 'techsee.me') => {
        const domainZoneSuffix = domainSuffix ? `${domainSuffix}.` : '';

        return `https://{{subdomain}}.${domainZoneSuffix}${host}/app/terms/techsee/${termsFileName}`;
    },

    whitelistDomain(url, validDomains) {
        const domain = this.getDomainFromUrl(url);

        return domain && !!find(validDomains, (validDomain) => endsWith(domain, validDomain));
    },

    reportToMomentDateFormat: function (reportDateFormat) {
        // dataFormat capitalization is needed for some format components
        // because of moment library format tokens
        let momentDateFormat = reportDateFormat.replace(/y/g, 'Y');

        momentDateFormat = momentDateFormat.replace(/d/g, 'D');

        return momentDateFormat;
    },

    catchAndLog: function (fn, msg, errData) {
        return (...args) =>
            fn(...args).catch((e) => {
                errData.error = e;
                errData.msg = msg;

                return Promise.reject(errData);
            });
    },

    isInIframe: function (win) {
        try {
            return win.self !== win.top;
        } catch (e) {
            return true;
        }
    },

    sanitizeCountryCode: function (countryCode) {
        if (!countryCode) {
            return '';
        }

        const updatedCountryCode = countryCode.substring(countryCode.search(patterns.numberOneToNine));

        return new RegExp(patterns.validCountryCode).test(`+${updatedCountryCode}`) ? updatedCountryCode : '';
    },

    isAudioAvailableInMode(mode) {
        return _isModeSupported(mode);
    },

    isVideoAvailableInMode(mode) {
        return _isModeSupported(mode);
    },

    isModeSupportNativeVideoSharing(mode) {
        return [MeetingMode.appSharing, MeetingMode.screen, MeetingMode.videoApplication].includes(mode);
    },

    isAccountSupportVideoApplication(sharingType, nativeAppId) {
        return (
            [hostingAppEnum.VA, hostingAppEnum.BT].includes(nativeAppId) &&
            sharingType.includes(screenShareTypeEnum.VIDEO_APPLICATION)
        );
    },

    isAccountSupportScreenSharing(sharingType, nativeAppId) {
        return (
            [hostingAppEnum.SA, hostingAppEnum.BT, hostingAppEnum.VA].includes(nativeAppId) &&
            sharingType.includes(screenShareTypeEnum.SCREEN)
        );
    },

    getBucketFromConfig(config) {
        return `${config.bucketNamePrefix}${config.region}${config.accountFolderSuffix || constants.HOST_SUFFIX}`;
    },

    isValidHttpUrl(string) {
        let url;

        try {
            url = new URL(string);
        } catch (e) {
            return false;
        }

        return includes(['https:', 'http:'], url.protocol);
    }
});
